import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Globe, Link2, Swords, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { GameIcon } from '@/components/ui/GameIcon';
import { GAMES } from '@/lib/gameConfig';
import { api, CreateWagerInput } from '@/lib/api';
import { useFriends } from '@/hooks/useFriends';
import { cn } from '@/lib/utils';

const games = GAMES.filter((g, i, arr) =>
  arr.findIndex((x) => x.name === g.name) === i
).slice(0, 12);

const wagerTypes = [
  { type: 'match_winner', name: 'Match Winner', description: 'Traditional win/lose' },
  { type: 'kill_race', name: 'Kill Race', description: 'Most kills wins' },
  { type: 'first_to_kills', name: 'First to X Kills', description: 'First to reach target' },
  { type: 'score_race', name: 'Score Race', description: 'Higher score wins' },
];

const amounts = [5, 10, 25, 50, 100];

const steps = ['Game', 'Type', 'Stake', 'Review'];

type VisibilityMode = 'public' | 'private' | 'friend';

export function CreateWagerPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);

  // Pre-fill from URL params (e.g. from friends list challenge button)
  const preselectedOpponent = searchParams.get('opponent');
  const preselectedOpponentName = searchParams.get('opponentName');

  const [form, setForm] = useState<CreateWagerInput>({
    gameCode: '',
    wagerType: 'match_winner',
    amount: '25',
    visibility: preselectedOpponent ? 'private' : 'public',
    targetOpponentId: preselectedOpponent || undefined,
  });
  const [visibilityMode, setVisibilityMode] = useState<VisibilityMode>(
    preselectedOpponent ? 'friend' : 'public'
  );
  const [targetOpponentName, setTargetOpponentName] = useState(
    preselectedOpponentName || ''
  );
  const [customAmount, setCustomAmount] = useState('');

  const { data: gameAccounts } = useQuery({
    queryKey: ['game-accounts'],
    queryFn: () => api.getGameAccounts(),
  });

  const { data: friendsList = [] } = useFriends();

  const createMutation = useMutation({
    mutationFn: (data: CreateWagerInput) => api.createWager(data),
    onSuccess: (wager) => {
      navigate(`/wagers/${wager.id}`);
    },
  });

  const linkedGameCodes = gameAccounts?.map((a) => a.gameCode) || [];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      createMutation.mutate(form);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    } else {
      navigate(-1);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return form.gameCode !== '';
      case 1:
        return form.wagerType !== '';
      case 2:
        if (parseFloat(form.amount) <= 0) return false;
        if (visibilityMode === 'friend' && !form.targetOpponentId) return false;
        return true;
      default:
        return true;
    }
  };

  const handleVisibilityChange = (mode: VisibilityMode) => {
    setVisibilityMode(mode);
    if (mode === 'public') {
      setForm({ ...form, visibility: 'public', targetOpponentId: undefined });
      setTargetOpponentName('');
    } else if (mode === 'private') {
      setForm({ ...form, visibility: 'private', targetOpponentId: undefined });
      setTargetOpponentName('');
    } else {
      // 'friend' mode — keep targetOpponentId if already set
      setForm({ ...form, visibility: 'private' });
    }
  };

  const handleSelectFriend = (friendId: string, friendName: string) => {
    setForm({ ...form, visibility: 'private', targetOpponentId: friendId });
    setTargetOpponentName(friendName);
  };

  const handleClearFriend = () => {
    setForm({ ...form, targetOpponentId: undefined });
    setTargetOpponentName('');
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={handleBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">Create Challenge</h1>
          <p className="text-sm text-muted-foreground">
            Step {step + 1} of {steps.length}: {steps[step]}
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-2">
        {steps.map((s, i) => (
          <div
            key={s}
            className={cn(
              'h-1 flex-1 rounded-full transition-colors',
              i <= step ? 'bg-primary' : 'bg-muted'
            )}
          />
        ))}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Select Game</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                {games.map((game) => {
                  const isLinked = linkedGameCodes.includes(game.code);
                  const isDisabled = !isLinked || !!game.comingSoon;
                  return (
                    <button
                      key={game.code}
                      disabled={isDisabled}
                      onClick={() => setForm({ ...form, gameCode: game.code })}
                      className={cn(
                        'relative flex items-center gap-3 rounded-xl border p-4 text-left transition-all',
                        form.gameCode === game.code
                          ? 'border-primary bg-primary/10'
                          : 'hover:border-muted-foreground/50',
                        isDisabled && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      <GameIcon gameCode={game.code} size="sm" />
                      <div className="flex-1">
                        <p className="font-medium">{game.name}</p>
                        {game.comingSoon ? (
                          <p className="text-xs text-muted-foreground">Coming Soon</p>
                        ) : !isLinked ? (
                          <p className="text-xs text-muted-foreground">Not linked</p>
                        ) : null}
                      </div>
                      {game.comingSoon && (
                        <Badge variant="secondary" className="text-xs">
                          Coming Soon
                        </Badge>
                      )}
                      {form.gameCode === game.code && (
                        <Check className="h-5 w-5 text-primary" />
                      )}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Challenge Type</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {wagerTypes.map((type) => (
                  <button
                    key={type.type}
                    onClick={() => setForm({ ...form, wagerType: type.type })}
                    className={cn(
                      'flex w-full items-center justify-between rounded-xl border p-4 text-left transition-all',
                      form.wagerType === type.type
                        ? 'border-primary bg-primary/10'
                        : 'hover:border-muted-foreground/50'
                    )}
                  >
                    <div>
                      <p className="font-medium">{type.name}</p>
                      <p className="text-sm text-muted-foreground">{type.description}</p>
                    </div>
                    {form.wagerType === type.type && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Stake</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {amounts.map((amount) => (
                    <Badge
                      key={amount}
                      variant={form.amount === String(amount) ? 'default' : 'outline'}
                      className="cursor-pointer px-4 py-2 text-lg"
                      onClick={() => {
                        setForm({ ...form, amount: String(amount) });
                        setCustomAmount('');
                      }}
                    >
                      ${amount}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">or</span>
                  <Input
                    type="number"
                    placeholder="Custom amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setForm({ ...form, amount: e.target.value });
                    }}
                    className="max-w-[150px]"
                  />
                  <span className="text-muted-foreground">USDC</span>
                </div>

                {/* Visibility — 3 options */}
                <div className="mt-6 space-y-3">
                  <p className="text-sm font-medium">Who can accept?</p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <button
                      onClick={() => handleVisibilityChange('public')}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-all hover:border-primary/50',
                        visibilityMode === 'public' && 'border-primary bg-primary/10'
                      )}
                    >
                      <Globe className="h-5 w-5" />
                      <span className="text-sm font-medium">Public</span>
                      <span className="text-xs text-muted-foreground">Anyone in the lobby</span>
                    </button>

                    <button
                      onClick={() => handleVisibilityChange('private')}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-all hover:border-primary/50',
                        visibilityMode === 'private' && 'border-primary bg-primary/10'
                      )}
                    >
                      <Link2 className="h-5 w-5" />
                      <span className="text-sm font-medium">Private Link</span>
                      <span className="text-xs text-muted-foreground">Share a link</span>
                    </button>

                    <button
                      onClick={() => handleVisibilityChange('friend')}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-xl border p-4 text-center transition-all hover:border-primary/50',
                        visibilityMode === 'friend' && 'border-primary bg-primary/10'
                      )}
                    >
                      <Swords className="h-5 w-5" />
                      <span className="text-sm font-medium">Invite Friend</span>
                      <span className="text-xs text-muted-foreground">Challenge directly</span>
                    </button>
                  </div>
                </div>

                {/* Friend Picker (only in "Invite Friend" mode) */}
                {visibilityMode === 'friend' && (
                  <div className="rounded-xl border p-4 space-y-3">
                    {form.targetOpponentId && targetOpponentName ? (
                      <div className="flex items-center gap-3 rounded-lg bg-primary/5 border border-primary/20 p-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {targetOpponentName[0]?.toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{targetOpponentName}</p>
                          <p className="text-xs text-muted-foreground">Will be notified when you create this challenge</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearFriend}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-sm font-medium">Select a friend to challenge</p>
                        <div className="max-h-48 overflow-y-auto space-y-2 sm:max-h-64">
                          {friendsList.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No friends yet. Add friends to challenge them directly.
                            </p>
                          ) : (
                            friendsList.map((friend) => (
                              <button
                                key={friend.id}
                                onClick={() => handleSelectFriend(friend.id, friend.displayName)}
                                className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent"
                              >
                                <Avatar className="h-8 w-8">
                                  <AvatarImage src={friend.avatarUrl || undefined} />
                                  <AvatarFallback>
                                    {friend.displayName[0]?.toUpperCase() || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="flex-1 text-sm font-medium">{friend.displayName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {friend.status === 'online' ? 'Online' : 'Offline'}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle>Review Challenge</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-xl bg-muted/50 p-4">
                  <div className="grid gap-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Game</span>
                      <span className="font-medium">
                        {games.find((g) => g.code === form.gameCode)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium">
                        {wagerTypes.find((t) => t.type === form.wagerType)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Stake</span>
                      <span className="font-medium">${form.amount} USDC</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Platform Fee</span>
                      <span className="text-muted-foreground">
                        ${(parseFloat(form.amount) * 0.01).toFixed(2)} (1%)
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Visibility</span>
                      <Badge variant="secondary" className="capitalize">
                        {visibilityMode === 'friend'
                          ? `Invite: ${targetOpponentName}`
                          : visibilityMode}
                      </Badge>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground">
                  {visibilityMode === 'friend' && targetOpponentName
                    ? `${targetOpponentName} will be notified and can accept your challenge directly.`
                    : `By creating this challenge, you agree to fund $${form.amount} USDC + 1% fee when an opponent accepts.`}
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex gap-4">
        <Button variant="outline" onClick={handleBack} className="flex-1">
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Button
          variant="gradient"
          onClick={handleNext}
          disabled={!canProceed() || createMutation.isPending}
          className="flex-1 gap-2"
        >
          {step === steps.length - 1 ? (
            createMutation.isPending ? (
              'Creating...'
            ) : (
              <>
                {visibilityMode === 'friend' ? 'Send Challenge' : 'Create Challenge'}{' '}
                <Check className="h-4 w-4" />
              </>
            )
          ) : (
            <>
              Next <ArrowRight className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
